import {
  createContext,
  createElement,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from 'react';

import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { DEFAULT_SBEF_CUSTOM_GROUPS } from '@features/sbef/custom-groups.model';
import { createSbefGroupingHelpers } from '@features/elements/helpers/element-grouping.helpers';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useProposals } from '@features/proposals/hooks/use-proposals.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import {
  useGetResultsPerGFA,
  useVersionResultRecord,
} from '@features/results/use-results.hooks';
import { useSelectedVersion } from '@client/store/ui';
import {
  useProposalsResultRecords,
  useElementListMaxValues,
} from './use-proposals.hooks';
import { computeElementListGroupBarScaleMax } from './element-list-group-bar-scale.helpers';
import type { ElementListGroupBarScaleMax } from './element-list-group-bar-scale.helpers';

/** CO₂ / cost bar scale for one list domain (SBEF groups vs product categories). */
export type GroupBarScalePair = {
  maxCo2: number;
  maxCost: number;
};

export type ElementListGroupBarScaleValue = ElementListGroupBarScaleMax;

const SBEF_GROUP_KEY_FN = createSbefGroupingHelpers(
  DEFAULT_SBEF_CUSTOM_GROUPS,
).getGroupKey;

const EMPTY_SCALE: GroupBarScalePair = { maxCo2: 0, maxCost: 0 };

const EMPTY: ElementListGroupBarScaleValue = {
  sbefGroupBarScale: EMPTY_SCALE,
  productCategoryGroupBarScale: EMPTY_SCALE,
};

const ElementListGroupBarScaleContext =
  createContext<ElementListGroupBarScaleValue | null>(null);

/**
 * Bar-scale maxima for SBEF group rows and product category headers.
 * Root-level results only; resolves proposals in
 * {@link getActiveRootElementIdsInProposal} for the selected proposal (and baseline).
 */
export const ElementListGroupBarScaleProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const version = useSelectedVersion();
  const selectedProposalId = useSelectedProposalId();
  const proposalsResultRecords = useProposalsResultRecords();
  const proposals = useProposals();
  const getResultsPerGFA = useGetResultsPerGFA();
  const displayCurrency = useDisplayCurrency();
  const baselineElementResultsRecord = useVersionResultRecord(version);
  const elementListMax = useElementListMaxValues();

  const limitProposalIds = useMemo(
    () => (selectedProposalId ? new Set([selectedProposalId]) : undefined),
    [selectedProposalId],
  );

  const value = useMemo((): ElementListGroupBarScaleValue => {
    if (!version) return EMPTY;

    const { sbefGroupBarScale, productCategoryGroupBarScale } =
      computeElementListGroupBarScaleMax({
        version,
        proposalsResultRecords,
        proposals,
        getSbefGroupKey: SBEF_GROUP_KEY_FN,
        getResultsPerGFA,
        limitProposalIds,
        baselineElementResultsRecord,
      });

    const sekToDisplayCost = (sekMax: number) =>
      displayCurrency.isEnabled && sekMax > 0
        ? getDisplayValueData(
            { value: sekMax, unit: 'sek' },
            {
              outputUnit: { unit: displayCurrency.current },
              notation: 'compact',
            },
          ).valueAsNumber
        : 0;

    return {
      sbefGroupBarScale: {
        maxCo2:
          sbefGroupBarScale.maxCo2 > 0
            ? sbefGroupBarScale.maxCo2
            : elementListMax.maxCo2,
        maxCost:
          sbefGroupBarScale.maxCost > 0
            ? sekToDisplayCost(sbefGroupBarScale.maxCost)
            : elementListMax.maxCost,
      },
      productCategoryGroupBarScale: {
        maxCo2:
          productCategoryGroupBarScale.maxCo2 > 0
            ? productCategoryGroupBarScale.maxCo2
            : elementListMax.maxCo2,
        maxCost:
          productCategoryGroupBarScale.maxCost > 0
            ? sekToDisplayCost(productCategoryGroupBarScale.maxCost)
            : elementListMax.maxCost,
      },
    };
  }, [
    version,
    proposalsResultRecords,
    proposals,
    getResultsPerGFA,
    limitProposalIds,
    displayCurrency,
    baselineElementResultsRecord,
    elementListMax,
  ]);

  return createElement(
    ElementListGroupBarScaleContext.Provider,
    { value },
    children,
  );
};

/**
 * Reads the shared group-header CO₂ / cost scale from {@link ElementListGroupBarScaleProvider}.
 * Returns zeros when used outside the provider.
 */
export const useElementListGroupBarScale = (): ElementListGroupBarScaleValue =>
  useContext(ElementListGroupBarScaleContext) ?? EMPTY;
